import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { showTaskLinkToast } from "../../lib/undoStack";
import { Task } from "../../types";

interface UpdateTaskVariables {
  id: string;
  updates: Partial<Task>;
  options?: {
    // The recurring-date guard (showing a confirmation dialog and re-running
    // the update) is the caller's responsibility. Pass this flag once the user
    // has confirmed, so the hook skips its own date-change check.
    confirmedRecurringDateChange?: boolean;
  };
}

interface UpdateTaskContext {
  previousTasks: Task[] | undefined;
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<boolean, Error, UpdateTaskVariables, UpdateTaskContext>({
    mutationFn: async ({ id, updates }) => {
      const dbUpdates: Record<string, unknown> = {};
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.description !== undefined)
        dbUpdates.description = updates.description;
      if (updates.priority !== undefined) dbUpdates.priority = updates.priority;
      if (updates.completed !== undefined)
        dbUpdates.completed = updates.completed;
      if (updates.scheduled_date !== undefined)
        dbUpdates.scheduled_date = updates.scheduled_date;
      if (updates.scheduled_time !== undefined)
        dbUpdates.scheduled_time = updates.scheduled_time;
      if (updates.deadline_date !== undefined)
        dbUpdates.deadline_date = updates.deadline_date;
      if (updates.duration !== undefined) dbUpdates.duration = updates.duration;
      if (updates.persistent !== undefined)
        dbUpdates.persistent = updates.persistent;
      if (updates.working_hours_only !== undefined)
        dbUpdates.working_hours_only = updates.working_hours_only;
      if (updates.recurrence_frequency !== undefined)
        dbUpdates.recurrence_frequency = updates.recurrence_frequency;
      if (updates.recurrence_interval !== undefined)
        dbUpdates.recurrence_interval = updates.recurrence_interval;
      if (updates.recurrence_day_of_week !== undefined)
        dbUpdates.recurrence_day_of_week = updates.recurrence_day_of_week;
      if (updates.recurrence_end_date !== undefined)
        dbUpdates.recurrence_end_date = updates.recurrence_end_date;
      if (updates.archived !== undefined) dbUpdates.archived = updates.archived;
      if (updates.list_id !== undefined) dbUpdates.list_id = updates.list_id;
      if (updates.order_position !== undefined)
        dbUpdates.order_position = updates.order_position;
      const revision = new Date().toISOString();
      dbUpdates.updated_at = revision;

      const { error } = await supabase
        .from("tasks")
        .update(dbUpdates)
        .eq("id", id);

      if (error) throw error;

      Sentry.addBreadcrumb({
        category: "task",
        message: "Task updated",
        data: { taskId: id },
        level: "info",
      });

      return true;
    },

    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.tasks });
      const previousTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks);

      // Resolve list display info when the list assignment changes
      const allTasks = previousTasks ?? [];
      const existingTask = allTasks.find((t) => t.id === id);
      const newListTask = updates.list_id
        ? allTasks.find((t) => t.list_id === updates.list_id)
        : null;

      // Left alone when the cache has not loaded: writing `[]` into it would
      // flip useTasks out of its pending state and drop the app's loading gate,
      // and onError's snapshot guard could not put it back.
      queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
        prev
          ? prev
              .map((t) =>
                t.id === id
                  ? {
                      ...t,
                      ...updates,
                      list: newListTask?.list ?? existingTask?.list ?? t.list,
                      listEmoji:
                        newListTask?.listEmoji ??
                        existingTask?.listEmoji ??
                        t.listEmoji,
                    }
                  : t
              )
              .sort((a, b) => a.order_position - b.order_position)
          : prev
      );

      return { previousTasks };
    },

    onError: (err, { id }, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(queryKeys.tasks, context.previousTasks);
      }
      Sentry.captureException(err, {
        extra: { mutation: "useUpdateTask", taskId: id },
      });
      const title =
        context?.previousTasks?.find((t) => t.id === id)?.title ?? "task";
      showTaskLinkToast({
        prefix: "Failed to update ",
        label: `"${title}"`,
        labelSuffix: ". Please try again.",
        type: "error",
        onLabelClick: () =>
          navigate({
            to: "/task/$taskId",
            params: { taskId: id },
            search: (prev: Record<string, unknown>) => prev,
          }),
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    },
  });
}
